import { GetUsersRequest, User } from "@/Stub/V1";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable, DataTablePassThroughOptions, DataTableStateEvent } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import React, { useMemo } from "react";
import { useGetUsers } from "@/Service/Api/V1/ApiHooks/Users/useGetUsers";
import { snakeToNormalCase, url } from "@/Helpers/general";
import { Button } from "primereact/button";
import useGetUsersFiltersQuery, { UserFilterType } from "@/Hooks/useGetUsersFiltersQuery";
import { useUpdateSearchParams } from "@/Hooks/useUpdateSearchParams";


type TableColumnDefinition = {
    label: string
    sortable?: boolean
    field?: string
    sortField?: string
    headerTooltip?: string
    body?: React.ReactNode | ((data: User, options: ColumnBodyOptions) => React.ReactNode)
};

const dataTablePtOptions: DataTablePassThroughOptions = {
    root: {
        className: 'datatable-base'
    }
};

const StyledWrap = styled.main`
    .content-header {
        padding: 1rem;
        border-bottom: var(--gray-300) 1px solid;

        &__title {
            margin: 0.25rem 0;
            font-weight: 600;
            font-size: 1.5rem;
        }

        &__description {
            font-size: 0.9rem;
            color: var(--gray-500);
        }
    }

    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .datatable-container {
        margin-top: 2rem;
        font-size: 0.8rem;
    }

    .datatable-base {
        font-size: 0.8rem;
        font-weight: 500;
    }

    .datatable-cell {
        &__clean-text {
            margin: 0;
        }
    }

    .angles-sort {
        margin-left: 0.3rem;

        &__icon {
            color: var(--primary-200);
            font-size: 0.8rem;
            font-weight: 600;

            &.active {
                color: var(--primary-500);
            }
        }
    }
`;


export type UserDatableProps = {
    requestParams?: Partial<GetUsersRequest>
};


const UserDatable = ({ requestParams }: UserDatableProps) => {
    const navigate = useNavigate();
    const {
        pageQuery,
        pageSizeQuery
    } = useGetUsersFiltersQuery();
    const updateUsersFilterHandler = useUpdateSearchParams();


    const customersRequest = useMemo<GetUsersRequest>(() => {
        return {
            ...requestParams,
            page: pageQuery ? parseInt(pageQuery) : 1,
            per_page: pageSizeQuery ? parseInt(pageSizeQuery) : 10,
        };
    }, [requestParams, pageQuery, pageSizeQuery]);

    const {
        data: usersData,
        isLoading: customersLoading
    } = useGetUsers({
        requestParams: customersRequest
    });
    


    const navigateToUser = (user: User) => {
        navigate(url('user', { userId: user.id }));
    };

    const actionsBodyTemplate = (user: User) => {
        return (
            <div className="flex gap-2">
                <Button
                    onClick={() => navigateToUser(user)}
                    label='View'
                />
            </div>
        );
    };



    const tableColumns: TableColumnDefinition[] = [
        {
            label: 'First Name',
            field: 'firstname',
            body: (user) => {
                return <>
                    {user.name ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Organisation',
            field: 'client.name',
            body: (user) => {
                return <>
                    {user.client?.name ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Is SSO User',
            field: 'is_non_sso_user',
            body: (user) => {
                return <>
                    {user.is_non_sso_user ? 'No' : 'Yes'}
                </>;

            }
        },
        {
            label: 'Email',
            field: 'email',
            body: (user) => {
                return <>
                    {user.email ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Roles',
            body: (user) => {
                return <div className="flex flex-column gap-1">
                    {snakeToNormalCase(user?.role?.replace('ng_', ''))}
                </div>;

            }
        },
        {
            label: 'Actions',
            body: actionsBodyTemplate
        }
    ];


    const onPagination = (e: DataTableStateEvent) => {
        updateUsersFilterHandler(UserFilterType.Page, ((e.first / e.rows) + 1).toString());
        updateUsersFilterHandler(UserFilterType.PageSize, e.rows.toString());
    };

    const first = pageSizeQuery && pageQuery ? Number(pageSizeQuery) * Number(pageQuery) - 10 : 0;
    const rows = pageSizeQuery ? Number(pageSizeQuery) : 10;

    return (
        <StyledWrap>
            <div className="content-container">
                <DataTable
                    lazy
                    loading={customersLoading}
                    emptyMessage="No customers found."
                    value={usersData?.data}
                    totalRecords={usersData?.meta.total}
                    paginator
                    rows={rows}
                    rowsPerPageOptions={[10, 20, 50]}
                    first={first}
                    pt={dataTablePtOptions}
                    onPage={e => {
                        onPagination(e);
                    }}
                >
                    {tableColumns.map((column, index) =>
                        <Column
                            key={`customers-column-${index}`}
                            field={column.field}
                            body={column.body}
                            header={column.label}
                            sortable={column.sortable}
                            sortField={column.sortField}
                            headerTooltip={column.headerTooltip}
                        />
                    )}
                </DataTable>
            </div>
        </StyledWrap>
    );
};

export default UserDatable;
